import { useState, useEffect, useMemo } from 'react';
import { sortByName } from '../lib/sortByName';
import { useTranslation } from 'react-i18next';
import { organizationsAPI, adminAPI, brandsAPI, alertHealthAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
    Building2,
    Search,
    Plus,
    Mail,
    Phone,
    MapPin,
    MoreVertical,
    Edit2,
    Trash2,
    Settings,
    Lock,
    Unlock,
    Link2,
    ExternalLink,
    Globe,
    CreditCard,
    HelpCircle,
    AlertTriangle,
    Tag,
    ClipboardList,
    Sparkles,
    BellOff
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { GoalReminderDefaultsBar } from '../components/goalgame/admin/GoalReminderDefaultsBar';
import PortalLogoUpload from '../components/branding/PortalLogoUpload';
import MasterBrandToggle from '../components/branding/MasterBrandToggle';
import { OperationalBaseToggle } from '../components/admin/OperationalBaseToggle';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import EnabledLanguagesPicker from '../components/shared/EnabledLanguagesPicker';
import SchedulingProfileEditor from '../components/admin/SchedulingProfileEditor';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';
import { Label } from '../components/ui/label';
import { EmptyState, HelpTooltip } from '../components/UsabilityComponents';
import { serializeOrganizationSettings } from '../utils/serializeOrganizationSettings';

const PLATFORMS = [
    { value: 'google_reviews', label: 'Google Reviews', placeholder: 'https://g.page/r/YOUR_BUSINESS_ID/review' },
    { value: 'facebook', label: 'Facebook Reviews', placeholder: 'https://facebook.com/YOUR_PAGE/reviews' },
    { value: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/YOUR_HANDLE' },
    { value: 'trustpilot', label: 'Trustpilot', placeholder: 'https://trustpilot.com/review/YOUR_BUSINESS' },
    { value: 'yelp', label: 'Yelp', placeholder: 'https://yelp.com/biz/YOUR_BUSINESS' },
    { value: 'tripadvisor', label: 'Tripadvisor', placeholder: 'https://tripadvisor.com/YOUR_BUSINESS' },
    { value: 'tiktok', label: 'TikTok', placeholder: 'https://www.tiktok.com/@YOUR_HANDLE' }
];

// MEDICAL ONLY — a clinic review site, offered to medical organizations on top
// of the shared list. Every other vertical (and an organization whose industry
// is not chosen yet) keeps exactly the list above.
const MEDICAL_PLATFORMS = [
    { value: 'ratemds', label: 'RateMDs', placeholder: 'https://www.ratemds.com/doctor-ratings/YOUR_PAGE' }
];

const platformsForVertical = (vertical) => (
    vertical === 'medical' ? [...PLATFORMS, ...MEDICAL_PLATFORMS] : PLATFORMS
);

const AdminClients = () => {
    const { t } = useTranslation();
    const [organizations, setOrganizations] = useState([]);
    // Fleet-wide alert-delivery health: which clients are currently deaf.
    const [alertHealth, setAlertHealth] = useState(null);
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [brandFilter, setBrandFilter] = useState('all');
    
    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPlatformModalOpen, setIsPlatformModalOpen] = useState(false);
    const [isUrlsModalOpen, setIsUrlsModalOpen] = useState(false);
    const [isAllCardsPlatformModalOpen, setIsAllCardsPlatformModalOpen] = useState(false);
    const [isPermanentDeleteModalOpen, setIsPermanentDeleteModalOpen] = useState(false);
    const [orgToDelete, setOrgToDelete] = useState(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [editingOrg, setEditingOrg] = useState(null);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedPlatform, setSelectedPlatform] = useState('');
    const [allCardsPlatform, setAllCardsPlatform] = useState('');
    const [changingAllCards, setChangingAllCards] = useState(false);
    const [platformUrls, setPlatformUrls] = useState({});
    const [savingUrls, setSavingUrls] = useState(false);
    
    const [formData, setFormData] = useState({
        name: '',
        display_name: '',
        contact_email: '',
        phone: '',
        address: '',
        brand_id: '',
        vertical: ''
    });

    // Single-source-of-truth dirty detection (see /app/frontend/src/utils/serializeOrganizationSettings.js).
    // Snapshot is captured at modal-open time; `isOrgDirty` recomputes on every form change.
    const [orgSettingsSnapshot, setOrgSettingsSnapshot] = useState(null);

    const { isSuperAdmin, isPartner } = useAuth();

    const isOrgDirty = useMemo(() => {
        if (!orgSettingsSnapshot) return false;
        const current = serializeOrganizationSettings(formData, 'state');
        return current !== orgSettingsSnapshot;
    }, [formData, orgSettingsSnapshot]);

    const isEditingOrg = Boolean(editingOrg?.id);

    useEffect(() => {
        if (!isSuperAdmin() && !isPartner()) {
            toast.error('Access denied');
            return;
        }
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [orgsResponse, brandsResponse] = await Promise.all([
                organizationsAPI.list(),
                brandsAPI.list()
            ]);
            setOrganizations(sortByName(orgsResponse.data));
            setBrands(brandsResponse.data);
            try {
                const h = await alertHealthAPI.summary(true);
                setAlertHealth(h.data);
            } catch (_e) { setAlertHealth(null); }
        } catch (error) {
            toast.error('Failed to load organizations');
        } finally {
            setLoading(false);
        }
    };

    const openModal = (org = null) => {
        if (org) {
            setEditingOrg(org);
            const next = {
                name: org.name,
                display_name: org.display_name || '',
                contact_email: org.contact_email,
                phone: org.phone || '',
                address: org.address || '',
                brand_id: org.brand_id || '',
                vertical: org.vertical || ''
            };
            setFormData(next);
            setOrgSettingsSnapshot(serializeOrganizationSettings(org, 'org'));
        } else {
            setEditingOrg(null);
            const blank = {
                name: '',
                display_name: '',
                contact_email: '',
                phone: '',
                address: '',
                brand_id: '',
                vertical: ''
            };
            setFormData(blank);
            setOrgSettingsSnapshot(serializeOrganizationSettings(blank, 'state'));
        }
        setIsModalOpen(true);
    };

    const openPlatformModal = (org) => {
        setSelectedOrg(org);
        setSelectedPlatform(org.default_platform || 'google_reviews');
        setIsPlatformModalOpen(true);
    };

    const openUrlsModal = (org) => {
        setSelectedOrg(org);
        // Initialize with existing URLs or empty strings
        setPlatformUrls(org.platform_urls || {});
        setIsUrlsModalOpen(true);
    };

    const openAllCardsPlatformModal = (org) => {
        setSelectedOrg(org);
        setAllCardsPlatform(org.default_platform || 'google_reviews');
        setIsAllCardsPlatformModalOpen(true);
    };

    const handleChangeAllCardsPlatform = async () => {
        if (!selectedOrg || !allCardsPlatform) {
            toast.error('Please select a platform');
            return;
        }
        
        setChangingAllCards(true);
        try {
            const result = await adminAPI.updateAllCardsPlatform(selectedOrg.id, allCardsPlatform);
            toast.success(result.data.message);
            setIsAllCardsPlatformModalOpen(false);
            setAllCardsPlatform('');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update cards platform');
        } finally {
            setChangingAllCards(false);
        }
    };

    const handleSaveUrls = async () => {
        if (!selectedOrg) return;
        
        setSavingUrls(true);
        try {
            // Filter out empty URLs
            const cleanedUrls = {};
            Object.entries(platformUrls).forEach(([key, value]) => {
                if (value && value.trim()) {
                    cleanedUrls[key] = value.trim();
                }
            });
            
            await adminAPI.updatePlatformUrls(selectedOrg.id, cleanedUrls);
            toast.success('Review URLs updated successfully');
            setIsUrlsModalOpen(false);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update URLs');
        } finally {
            setSavingUrls(false);
        }
    };

    const handleTogglePlatformLock = async (org) => {
        try {
            const newLockState = !org.platform_locked;
            await adminAPI.togglePlatformLock(org.id, newLockState);
            toast.success(newLockState ? 'Platform locked for organization' : 'Platform unlocked for organization');
            loadData();
        } catch (error) {
            toast.error('Failed to toggle platform lock');
        }
    };

    const handleToggleUrlRegistryAccess = async (org) => {
        try {
            const newState = !org.url_registry_access;
            await adminAPI.toggleUrlRegistryAccess(org.id, newState);
            toast.success(newState ? 'URL Registry access enabled' : 'URL Registry access disabled');
            loadData();
        } catch (error) {
            toast.error('Failed to toggle URL Registry access');
        }
    };

    const handleToggleSurveysModule = async (org) => {
        const newState = !org.surveys_module_enabled;
        // Confirm only when enabling — disabling is fast/safe.
        if (newState) {
            const ok = window.confirm(t('clients.surveysToggleConfirmEnable', { name: org.name }));
            if (!ok) return;
        }
        try {
            await organizationsAPI.toggleSurveysModule(org.id, newState);
            // Bust any cached value the sidebar hook might have for this org.
            try { sessionStorage.removeItem(`surveys_enabled_${org.id}`); } catch (_) {}
            toast.success(newState ? t('clients.surveysEnabled') : t('clients.surveysDisabled'));
            loadData();
        } catch (error) {
            toast.error(error?.response?.data?.detail || t('clients.surveysToggleFailed'));
        }
    };

    const handleToggleManageSurveysPermission = async (org) => {
        const newState = !org.allow_client_manage_surveys;
        try {
            await organizationsAPI.toggleManageSurveysPermission(org.id, newState);
            toast.success(newState ? t('clients.manageSurveysGranted') : t('clients.manageSurveysRevoked'));
            loadData();
        } catch (error) {
            toast.error(error?.response?.data?.detail || t('clients.manageSurveysToggleFailed'));
        }
    };

    const handleEnabledLanguagesChange = async (org, next) => {
        const list = Array.isArray(next) && next.length ? next : ['en', 'fr', 'es'];
        try {
            await organizationsAPI.updateEnabledLanguages(org.id, list);
            toast.success(t('languages.updated', 'Enabled languages updated'));
            loadData();
        } catch (error) {
            toast.error(error?.response?.data?.detail || t('languages.updateFailed', 'Failed to update enabled languages'));
        }
    };

    const handleSubmit = async () => {
        if (!formData.name.trim() || !formData.contact_email.trim()) {
            toast.error('Please fill in required fields');
            return;
        }

        const payload = {
            name: formData.name.trim(),
            display_name: formData.display_name.trim() || null,
            contact_email: formData.contact_email.trim(),
            phone: formData.phone.trim() || null,
            address: formData.address.trim() || null,
            brand_id: formData.brand_id || null,
        };

        try {
            if (editingOrg) {
                await organizationsAPI.update(editingOrg.id, payload);
                if ((formData.vertical || '') !== (editingOrg.vertical || '') && formData.vertical) {
                    await organizationsAPI.setVertical(editingOrg.id, formData.vertical);
                }
                toast.success('Organization updated successfully');
            } else {
                const created = await organizationsAPI.create(payload);
                const newId = created?.data?.id;
                if (newId && formData.vertical) {
                    await organizationsAPI.setVertical(newId, formData.vertical);
                }
                toast.success('Organization created successfully');
            }
            setIsModalOpen(false);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Operation failed');
        }
    };

    const handlePlatformChange = async () => {
        if (!selectedOrg || !selectedPlatform) return;

        try {
            await adminAPI.setOrganizationPlatform(selectedOrg.id, selectedPlatform);
            toast.success(`Platform updated to ${PLATFORMS.find(p => p.value === selectedPlatform)?.label}`);
            setIsPlatformModalOpen(false);
            loadData();
        } catch (error) {
            toast.error('Failed to update platform');
        }
    };

    const handleDelete = async (orgId) => {
        if (!window.confirm('Are you sure you want to deactivate this organization?')) {
            return;
        }

        try {
            await organizationsAPI.delete(orgId);
            toast.success('Organization deactivated');
            loadData();
        } catch (error) {
            toast.error('Failed to deactivate organization');
        }
    };

    const openPermanentDeleteModal = (org) => {
        setOrgToDelete(org);
        setIsPermanentDeleteModalOpen(true);
    };

    const handlePermanentDelete = async () => {
        if (!orgToDelete) return;

        setDeleting(true);
        try {
            const response = await organizationsAPI.permanentDelete(orgToDelete.id);
            const deleted = response.data.deleted;
            toast.success(
                `Permanently deleted "${orgToDelete.name}" and ${deleted.cards} cards, ${deleted.employees} employees, ${deleted.locations} locations`
            );
            setIsPermanentDeleteModalOpen(false);
            setOrgToDelete(null);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to permanently delete organization');
        } finally {
            setDeleting(false);
        }
    };

    // Which platform rows an organization gets. The industry CURRENTLY CHOSEN
    // in the open create/edit form wins over the saved value, so the RateMDs
    // row appears or disappears the moment the industry changes — no save and
    // no reopen. An industry that is not chosen yet gets the normal list.
    const verticalOf = (org) => (
        org && editingOrg && editingOrg.id === org.id
            ? (formData.vertical || '')
            : (org?.vertical || '')
    );
    const platformsForOrg = (org) => platformsForVertical(verticalOf(org));

    const getPlatformBadge = (platform) => {
        const colors = {
            google_reviews: 'bg-fuchsia-100 text-fuchsia-700',
            facebook: 'bg-indigo-100 text-indigo-700',
            instagram: 'bg-pink-100 text-pink-700',
            trustpilot: 'bg-green-100 text-green-700',
            yelp: 'bg-red-100 text-red-700'
        };
        const label = [...PLATFORMS, ...MEDICAL_PLATFORMS].find(p => p.value === platform)?.label || platform;
        return (
            <Badge className={`${colors[platform] || 'bg-slate-100 text-muted-foreground'}`}>
                {label}
            </Badge>
        );
    };

    const filteredOrgs = organizations.filter(org => {
        const matchesSearch = (org.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (org.contact_email || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesBrand = brandFilter === 'all' || org.brand_id === brandFilter || (!org.brand_id && brandFilter === 'none');
        return matchesSearch && matchesBrand;
    });

    // A partner reads this screen; every write control inside is absent for them.
    if (!isSuperAdmin() && !isPartner()) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <p className="text-muted-foreground">Access denied. Super Admin only.</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="skeleton h-20 rounded-xl" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="skeleton h-48 rounded-xl" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-clients-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {t('clients.pageTitle')}
                        <HelpTooltip text="Organizations are your client accounts (e.g., restaurant chains or franchise groups). Each organization can have multiple locations." />
                    </h1>
                    <p className="text-muted-foreground mt-1">{t('clients.pageSubtitle')}</p>
                </div>
                <TooltipProvider>
                    {!isPartner() && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button 
                                onClick={() => openModal()}
                                className="bg-blue-600 hover:bg-blue-700"
                                data-testid="add-client-btn"
                                data-insight-id="admin-clients.workflow.create"
                                data-insight-label="Create a new organization"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                {t('clients.addOrgBtn')}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p className="text-sm">Create a new organization to manage their locations and cards</p>
                        </TooltipContent>
                    </Tooltip>
                    )}
                </TooltipProvider>
            </div>

            {/* Alert-delivery health across the whole fleet. Empty recipient
                lists are silent by design, so this is the only surface that
                counts the clients currently receiving nothing. */}
            {alertHealth && alertHealth.deaf > 0 && (
                <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4" data-testid="fleet-alert-health">
                    <div className="flex items-start gap-3">
                        <BellOff className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-amber-700 dark:text-amber-200" data-testid="fleet-alert-health-title">
                                {t('alertHealth.fleetTitle', '{{deaf}} of {{total}} clients are receiving no alerts', { deaf: alertHealth.deaf, total: alertHealth.organizations_total })}
                            </p>
                            <p className="text-xs text-amber-700/80 dark:text-amber-100/80 mt-1">
                                {t('alertHealth.fleetBody', 'They have no recipient on any list, so taps, enquiries and test-drive requests are recorded but never delivered.')}
                            </p>
                            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="fleet-alert-health-list">
                                {(alertHealth.organizations || []).map((o) => (
                                    <Badge key={o.organization_id} variant="outline"
                                        className="text-xs bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700"
                                        data-testid={`fleet-deaf-${o.organization_id}`}>
                                        {o.organization_name}{o.vehicles_total ? ` · ${o.deaf_vehicles}/${o.vehicles_total}` : ''}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Tenant Setup Guide — calm explainer of the org/brand/user flow */}
            <Card className="bg-gradient-to-br from-blue-500/5 to-purple-500/5 border-blue-500/15">
                <CardContent className="p-5 sm:p-6">
                    <h2 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2" data-testid="tenant-setup-guide-title">
                        <Sparkles className="w-4 h-4 text-blue-400" />
                        {t('clients.setupGuide.title')}
                    </h2>
                    <ol className="space-y-2.5 text-sm text-muted-foreground">
                        <li className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500/15 text-blue-400 text-xs flex items-center justify-center font-medium">1</span>
                            <span>
                                {t('clients.setupGuide.step1')}{' '}
                                <a href="/admin/brands" className="text-blue-400 hover:underline">{t('clients.setupGuide.step1_link')} →</a>
                            </span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500/15 text-blue-400 text-xs flex items-center justify-center font-medium">2</span>
                            <span>{t('clients.setupGuide.step2')}</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500/15 text-blue-400 text-xs flex items-center justify-center font-medium">3</span>
                            <span>
                                {t('clients.setupGuide.step3')}{' '}
                                <a href="/admin/users" className="text-blue-400 hover:underline">{t('clients.setupGuide.step3_link')} →</a>
                            </span>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500/15 text-blue-400 text-xs flex items-center justify-center font-medium">4</span>
                            <span>{t('clients.setupGuide.step4')}</span>
                        </li>
                    </ol>
                    <p className="mt-4 pt-3 border-t border-blue-500/10 text-xs text-blue-300/80 italic" data-testid="tenant-setup-guide-demo-hint">
                        {t('clients.setupGuide.demo_hint')}
                    </p>
                </CardContent>
            </Card>

            {/* Search and Filter */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder={t('clients.searchByOrgOrEmail')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                                data-testid="search-clients-input"
                            />
                        </div>
                        <Select value={brandFilter} onValueChange={setBrandFilter}>
                            <SelectTrigger className="w-full sm:w-[200px]" data-testid="brand-filter">
                                <Tag className="w-4 h-4 mr-2 text-muted-foreground" />
                                <SelectValue placeholder={t('clients.filterByBrand')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t('clients.allBrands')}</SelectItem>
                                <SelectItem value="none">{t('clients.noBrand')}</SelectItem>
                                {brands.map((brand) => (
                                    <SelectItem key={brand.id} value={brand.id}>
                                        {brand.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Clients Grid */}
            {filteredOrgs.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredOrgs.map((org, idx) => (
                        <Card 
                            key={org.id} 
                            className={`card-hover ${!org.is_active ? 'opacity-60' : ''}`}
                            data-testid={`client-${org.id}`}
                        >
                            <CardContent className="p-5">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                                            {org.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <p className="font-semibold text-foreground dark:text-white">{org.name}</p>
                                            {org.brand_name && (
                                                <Badge variant="outline" className="text-xs mt-1 bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700">
                                                    <Tag className="w-3 h-3 mr-1" />
                                                    {org.brand_name}
                                                </Badge>
                                            )}
                                            {!org.is_active && (
                                                <Badge variant="destructive" className="text-xs mt-1 ml-1">{t('common.inactive')}</Badge>
                                            )}
                                            {(alertHealth?.organizations || []).some((o) => o.organization_id === org.id) && (
                                                <Badge variant="outline" className="text-xs mt-1 ml-1 bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700"
                                                    data-testid={`org-no-alerts-${org.id}`}>
                                                    <BellOff className="w-3 h-3 mr-1" />
                                                    {t('alertHealth.chip', 'No alert recipients')}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    {!isPartner() && (
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`org-actions-${org.id}`}>
                                                <MoreVertical className="w-4 h-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem onClick={() => openModal(org)}>
                                                <Edit2 className="w-4 h-4 mr-2" />
                                                Edit Details
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => openUrlsModal(org)}>
                                                <Link2 className="w-4 h-4 mr-2" />
                                                Configure Review URLs
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem onClick={() => openAllCardsPlatformModal(org)}
                                                data-insight-id={idx === 0 ? "admin-clients.workflow.all-cards-platform" : undefined}
                                                data-insight-label={idx === 0 ? "Bulk platform override for every card at this organization" : undefined}
                                            >
                                                <CreditCard className="w-4 h-4 mr-2" />
                                                Change ALL Cards Platform
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => openPlatformModal(org)}>
                                                <Settings className="w-4 h-4 mr-2" />
                                                Change Default Platform
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => handleTogglePlatformLock(org)}>
                                                {org.platform_locked ? (
                                                    <>
                                                        <Unlock className="w-4 h-4 mr-2" />
                                                        {t('clients.unlockPlatform')}
                                                    </>
                                                ) : (
                                                    <>
                                                        <Lock className="w-4 h-4 mr-2" />
                                                        {t('clients.lockPlatform')}
                                                    </>
                                                )}
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem 
                                                onClick={() => handleDelete(org.id)}
                                                className="text-amber-600"
                                                data-testid={`deactivate-org-${org.id}`}
                                                data-insight-id={idx === 0 ? "admin-clients.workflow.deactivate" : undefined}
                                                data-insight-label={idx === 0 ? "Deactivate organization (reversible)" : undefined}
                                            >
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                Deactivate
                                            </DropdownMenuItem>
                                            <DropdownMenuItem 
                                                onClick={() => openPermanentDeleteModal(org)}
                                                className="text-red-600 font-medium"
                                                data-testid={`permanent-delete-org-${org.id}`}
                                                data-insight-id={idx === 0 ? "admin-clients.workflow.permanent-delete" : undefined}
                                                data-insight-label={idx === 0 ? "Permanently delete organization" : undefined}
                                            >
                                                <AlertTriangle className="w-4 h-4 mr-2" />
                                                Permanent Delete
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                    )}
                                </div>

                                <div className="space-y-2 mb-4">
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <Mail className="w-4 h-4 text-muted-foreground" />
                                        <span className="truncate">{org.contact_email}</span>
                                    </div>
                                    {org.phone && (
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <Phone className="w-4 h-4 text-muted-foreground" />
                                            <span>{org.phone}</span>
                                        </div>
                                    )}
                                    {org.address && (
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <MapPin className="w-4 h-4 text-muted-foreground" />
                                            <span className="truncate">{org.address}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="pt-4 border-t border-slate-100 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">Platform:</span>
                                        {getPlatformBadge(org.default_platform)}
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">Custom URLs:</span>
                                        {org.platform_urls && Object.keys(org.platform_urls).length > 0 ? (
                                            <Badge className="bg-emerald-100 text-emerald-700">
                                                <Link2 className="w-3 h-3 mr-1" />
                                                {Object.keys(org.platform_urls).length} configured
                                            </Badge>
                                        ) : (
                                            <Badge variant="outline" className="text-muted-foreground">
                                                Using defaults
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">Client can change:</span>
                                        <div className="flex items-center gap-2">
                                            {org.platform_locked ? (
                                                <Badge className="bg-red-100 text-red-700">
                                                    <Lock className="w-3 h-3 mr-1" />
                                                    Locked
                                                </Badge>
                                            ) : (
                                                <Badge className="bg-green-100 text-green-700">
                                                    <Unlock className="w-3 h-3 mr-1" />
                                                    Unlocked
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">URL Registry:</span>
                                        <Switch
                                            checked={org.url_registry_access || false}
                                            onCheckedChange={() => handleToggleUrlRegistryAccess(org)}
                                            data-testid={`url-registry-toggle-${org.id}`}
                                        />
                                    </div>
                                    <div className="flex items-center justify-between" data-testid={`surveys-module-row-${org.id}`}>
                                        <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                                            <ClipboardList className="w-3.5 h-3.5" />
                                            {t('clients.surveysModule')}
                                            {org.surveys_module_enabled && (
                                                <Badge className="bg-fuchsia-100 dark:bg-fuchsia-900/40 text-fuchsia-700 dark:text-fuchsia-300 text-[10px] px-1.5 py-0 ml-1">
                                                    {t('common.active')}
                                                </Badge>
                                            )}
                                        </span>
                                        <Switch
                                            checked={!!org.surveys_module_enabled}
                                            onCheckedChange={() => handleToggleSurveysModule(org)}
                                            data-testid={`surveys-module-toggle-${org.id}`}
                                            data-insight-id={idx === 0 ? "admin-clients.workflow.module-toggle-surveys" : undefined}
                                            data-insight-label={idx === 0 ? "Per-organization Surveys module capability" : undefined}
                                        />
                                    </div>
                                    {org.surveys_module_enabled && (
                                        <div className="flex items-center justify-between pl-5" data-testid={`manage-surveys-row-${org.id}`}>
                                            <span className="text-xs text-muted-foreground italic">
                                                ↳ {t('clients.allowManageSurveys')}
                                            </span>
                                            <Switch
                                                checked={!!org.allow_client_manage_surveys}
                                                onCheckedChange={() => handleToggleManageSurveysPermission(org)}
                                                data-testid={`manage-surveys-toggle-${org.id}`}
                                            />
                                        </div>
                                    )}
                                    {/* TAP'N WIN — the reminder every new player
                                        at this organization starts with. Only
                                        for an organization that runs Tap'N Win. */}
                                    {org.goal_game_enabled && (
                                        <GoalReminderDefaultsBar organizationId={org.id} compact />
                                    )}
                                    <div className="pt-3 border-t border-slate-100 dark:border-slate-700" data-testid={`enabled-langs-row-${org.id}`}>
                                        <EnabledLanguagesPicker
                                            value={Array.isArray(org.enabled_languages) ? org.enabled_languages : ['en','fr','es']}
                                            onChange={(next) => handleEnabledLanguagesChange(org, next)}
                                            testIdPrefix={`org-languages-${org.id}`}
                                        />
                                    </div>
                                    <SchedulingProfileEditor
                                        org={org}
                                        onSaved={(updated) => {
                                            setOrganizations((prev) =>
                                                prev.map((o) => o.id === org.id ? { ...o, ...updated } : o)
                                            );
                                        }}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <Card>
                    <CardContent className="py-16">
                        <EmptyState
                            icon={Building2}
                            title={searchTerm ? "No organizations found" : "No organizations yet"}
                            description={
                                searchTerm 
                                    ? "Try adjusting your search"
                                    : "Organizations are your client accounts. Each organization can have multiple locations and employees using Tap Engine Cards."
                            }
                            action={
                                !searchTerm && !isPartner() && (
                                    <Button onClick={() => openModal()}>
                                        <Plus className="w-4 h-4 mr-2" />
                                        Add Your First Organization
                                    </Button>
                                )
                            }
                            tip={!searchTerm && "After creating an organization, you can add locations and assign cards to employees"}
                        />
                    </CardContent>
                </Card>
            )}

            {/* Create/Edit Modal */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="max-w-lg w-[95vw] sm:w-full max-h-[90vh] overflow-hidden grid grid-rows-[auto_1fr_auto] p-0">
                    <DialogHeader className="px-6 pt-6 pb-2">
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {editingOrg ? t('clients.editOrg') : t('clients.addOrg')}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="overflow-y-auto px-6 pb-2">
                    <p className="text-xs text-muted-foreground">{t('common.requiredFields')}</p>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="mb-2 block">{t('clients.businessName')} <span className="text-red-500">*</span></Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder={t('clients.businessNamePlaceholder')}
                                data-testid="client-name-input"
                            />
                        </div>
                        <div>
                            <Label className="mb-2 block">{t('clients.displayName')} <span className="text-muted-foreground text-xs">{t('clients.displayNameHint')}</span></Label>
                            <Input
                                value={formData.display_name}
                                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                                placeholder={t('clients.displayNamePlaceholder')}
                                data-testid="client-display-name-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                {t('clients.displayNameOverride')}
                            </p>
                        </div>
                        <div>
                            <Label className="mb-2 block">{t('clients.brand')} <span className="text-muted-foreground text-xs">{t('common.optionalParen')}</span></Label>
                            <Select 
                                value={formData.brand_id || "none"} 
                                onValueChange={(val) => setFormData({ ...formData, brand_id: val === "none" ? "" : val })}
                            >
                                <SelectTrigger data-testid="client-brand-select" data-insight-id="admin-clients.workflow.brand-assignment" data-insight-label="Brand assignment">
                                    <SelectValue placeholder={t('clients.selectBrand')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">{t('clients.noBrand')}</SelectItem>
                                    {brands.map((brand) => (
                                        <SelectItem key={brand.id} value={brand.id}>
                                            {brand.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground mt-1">
                                {t('clients.brandHelp')}
                            </p>
                        </div>
                        <div>
                            <Label className="mb-2 block">{t('clients.businessType')} <span className="text-muted-foreground text-xs">{t('common.optionalParen')}</span></Label>
                            <Select
                                value={formData.vertical || "unset"}
                                onValueChange={(val) => setFormData({ ...formData, vertical: val === "unset" ? "" : val })}
                            >
                                <SelectTrigger data-testid="client-business-type-select">
                                    <SelectValue placeholder={t('clients.businessTypeSelect')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="unset">{t('clients.businessTypeAuto')}</SelectItem>
                                    <SelectItem value="restaurant">{t('clients.businessTypeRestaurant')}</SelectItem>
                                    <SelectItem value="hotel">{t('clients.businessTypeHotel')}</SelectItem>
                                    <SelectItem value="property_management">{t('clients.businessTypePropertyManagement')}</SelectItem>
                                    <SelectItem value="automotive">{t('clients.businessTypeAutomotive')}</SelectItem>
                                    <SelectItem value="grocery">{t('clients.businessTypeGrocery')}</SelectItem>
                                    <SelectItem value="medical">{t('clients.businessTypeMedical')}</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground mt-1">
                                {t('clients.businessTypeHelp')}
                            </p>
                        </div>
                        <div>
                            <Label className="mb-2 block">{t('clients.contactEmail')} <span className="text-red-500">*</span></Label>
                            <Input
                                type="email"
                                value={formData.contact_email}
                                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                                placeholder={t('clients.emailPlaceholder')}
                                data-testid="client-email-input"
                            />
                        </div>
                        <div>
                            <Label className="mb-2 block">{t('clients.phone')} <span className="text-muted-foreground text-xs">{t('common.optionalParen')}</span></Label>
                            <Input
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                placeholder={t('clients.phonePlaceholder')}
                                data-testid="client-phone-input"
                            />
                        </div>
                        <div>
                            <Label className="mb-2 block">{t('clients.address')} <span className="text-muted-foreground text-xs">{t('common.optionalParen')}</span></Label>
                            <Input
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                placeholder={t('clients.addressPlaceholder')}
                                data-testid="client-address-input"
                            />
                        </div>

                        {/* v4.30 — Portal identity (org-scoped). Only available
                          * once the org exists (we need an org_id for the upload
                          * endpoint). For new orgs the operator saves first, then
                          * reopens the modal to upload logos. */}
                        <PortalLogoUpload
                            endpoint={isEditingOrg
                                ? `/organizations/${editingOrg.id}/portal-logo`
                                : ''}
                            deleteEndpoint={isEditingOrg
                                ? `/organizations/${editingOrg.id}/portal-logo`
                                : null}
                            currentLight={editingOrg?.portal_logo_url || null}
                            currentDark={editingOrg?.portal_logo_dark_url || null}
                            disabled={!isEditingOrg}
                            kind="org"
                            previewOrgId={editingOrg?.id || null}
                            previewOrgName={
                                editingOrg?.display_name
                                || editingOrg?.name
                                || formData.display_name
                                || formData.name
                                || null
                            }
                            onUploaded={({ variant, logo_url }) => {
                                setEditingOrg((prev) => prev ? {
                                    ...prev,
                                    [variant === 'dark' ? 'portal_logo_dark_url' : 'portal_logo_url']: logo_url,
                                } : prev);
                            }}
                            onRemoved={({ variant }) => {
                                setEditingOrg((prev) => prev ? {
                                    ...prev,
                                    [variant === 'dark' ? 'portal_logo_dark_url' : 'portal_logo_url']: null,
                                } : prev);
                            }}
                        />
                        <MasterBrandToggle
                            orgId={isEditingOrg ? editingOrg.id : null}
                            hasPortalLogo={!!(editingOrg?.portal_logo_url || editingOrg?.portal_logo_dark_url)}
                        />
                        {/* Operational Base — per-org switch, default OFF. */}
                        {isEditingOrg && <OperationalBaseToggle orgId={editingOrg.id} />}
                    </div>
                    </div>
                    <DialogFooter className="px-6 pb-6 pt-3 border-t border-slate-200 dark:border-slate-700 bg-background flex-wrap gap-2">
                        {isEditingOrg && (
                            <span
                                data-testid="org-dirty-indicator"
                                className={`text-xs font-medium ${isOrgDirty ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300'}`}
                                style={{ marginRight: 'auto' }}
                            >
                                {isOrgDirty
                                    ? t('common.pendingChanges')
                                    : t('common.allSettingsSaved')}
                            </span>
                        )}
                        <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button onClick={handleSubmit} data-testid="save-client-btn">
                            {editingOrg ? t('common.update') : t('common.create')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Platform Change Modal */}
            <Dialog open={isPlatformModalOpen} onOpenChange={setIsPlatformModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Change Default Platform for {selectedOrg?.name}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <Label className="mb-2 block">Select Platform</Label>
                        <p className="text-sm text-muted-foreground mb-3">
                            This will update the redirect platform for all cards belonging to this client.
                        </p>
                        <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
                            <SelectTrigger data-testid="select-platform">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {platformsForOrg(selectedOrg).map(platform => (
                                    <SelectItem key={platform.value} value={platform.value}>
                                        {platform.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsPlatformModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handlePlatformChange} data-testid="update-platform-btn">
                            Update Platform
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Configure Review URLs Modal */}
            <Dialog open={isUrlsModalOpen} onOpenChange={setIsUrlsModalOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Configure Review URLs for {selectedOrg?.name}
                        </DialogTitle>
                        <DialogDescription>
                            Set custom review page URLs for each platform. When customers tap a card, 
                            they&apos;ll be redirected to these specific pages instead of generic platform homepages.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-4 max-h-[60vh] overflow-y-auto">
                        {platformsForOrg(selectedOrg).map(platform => (
                            <div key={platform.value} className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    {platform.label}
                                    {platformUrls[platform.value] && (
                                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                            Custom URL set
                                        </Badge>
                                    )}
                                </Label>
                                <div className="flex gap-2">
                                    <Input
                                        value={platformUrls[platform.value] || ''}
                                        onChange={(e) => setPlatformUrls({
                                            ...platformUrls,
                                            [platform.value]: e.target.value
                                        })}
                                        placeholder={platform.placeholder}
                                        className="flex-1"
                                        data-testid={`url-input-${platform.value}`}
                                    />
                                    {platformUrls[platform.value] && (
                                        <Button 
                                            variant="outline" 
                                            size="icon"
                                            onClick={() => window.open(platformUrls[platform.value], '_blank')}
                                            title={t('adminClients.testURL')}
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {platform.value === 'google_reviews' && 
                                        "Find this in Google Business Profile → Share review link"}
                                    {platform.value === 'facebook' && 
                                        "Use your Facebook page URL with /reviews at the end"}
                                    {platform.value === 'instagram' && 
                                        "Your Instagram profile URL"}
                                    {platform.value === 'trustpilot' && 
                                        "Your Trustpilot business page URL"}
                                    {platform.value === 'yelp' && 
                                        "Your Yelp business page URL"}
                                    {platform.value === 'ratemds' && 
                                        "Your clinic's RateMDs page URL"}
                                    {platform.value === 'tiktok' && 
                                        "Your TikTok profile URL"}
                                </p>
                            </div>
                        ))}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsUrlsModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleSaveUrls} 
                            disabled={savingUrls}
                            data-testid="save-urls-btn"
                        >
                            {savingUrls ? 'Saving...' : 'Save URLs'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Change ALL Cards Platform Modal */}
            <Dialog open={isAllCardsPlatformModalOpen} onOpenChange={setIsAllCardsPlatformModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <Globe className="w-5 h-5 inline mr-2 text-blue-600" />
                            Change Platform for ALL Cards - {selectedOrg?.name}
                        </DialogTitle>
                        <DialogDescription>
                            This will instantly change the platform for <strong>ALL cards</strong> belonging to this organization.
                            Use this when running a review campaign on a specific platform.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Label className="mb-2 block">Select New Platform</Label>
                        <Select value={allCardsPlatform} onValueChange={setAllCardsPlatform}>
                            <SelectTrigger data-testid="select-all-cards-platform">
                                <SelectValue placeholder="Choose a platform..." />
                            </SelectTrigger>
                            <SelectContent>
                                {platformsForOrg(selectedOrg).map(platform => (
                                    <SelectItem key={platform.value} value={platform.value}>
                                        {platform.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <div className="mt-4 p-3 bg-white dark:bg-slate-900/40 border rounded-lg" style={{ borderColor: 'rgba(245,130,32,0.45)' }}>
                            <p className="text-sm text-slate-700 dark:text-slate-200">
                                <strong>Note:</strong> This will update all cards for {selectedOrg?.name} to redirect to the selected platform.
                                The organization&apos;s default platform will also be updated.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsAllCardsPlatformModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleChangeAllCardsPlatform} 
                            disabled={changingAllCards || !allCardsPlatform}
                            className="bg-blue-600 hover:bg-blue-700"
                            data-testid="apply-all-cards-platform-btn"
                        >
                            {changingAllCards ? 'Updating...' : 'Apply to All Cards'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Permanent Delete Confirmation Modal */}
            <Dialog open={isPermanentDeleteModalOpen} onOpenChange={setIsPermanentDeleteModalOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Permanent Delete
                        </DialogTitle>
                        <DialogDescription className="text-muted-foreground">
                            This action is <strong>irreversible</strong>. All data will be permanently deleted.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {orgToDelete && (
                        <div className="py-4">
                            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm font-medium text-red-800 mb-2">
                                    You are about to permanently delete:
                                </p>
                                <p className="text-lg font-bold text-red-900">{orgToDelete.name}</p>
                                <ul className="text-sm text-red-700 mt-3 space-y-1">
                                    <li>• All locations</li>
                                    <li>• All cards and tap history</li>
                                    <li>• All employees</li>
                                    <li>• All platform settings</li>
                                </ul>
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button 
                            variant="outline" 
                            onClick={() => {
                                setIsPermanentDeleteModalOpen(false);
                                setOrgToDelete(null);
                            }}
                            disabled={deleting}
                        >
                            No, Cancel
                        </Button>
                        <Button 
                            variant="destructive"
                            onClick={handlePermanentDelete}
                            disabled={deleting}
                            data-testid="confirm-permanent-delete-btn"
                        >
                            {deleting ? 'Deleting...' : 'Yes, Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminClients;
